// @ts-check
const timePlaced = 'Time placed'
const maskType = 'Mask Type'
const peep = 'PEEP (in cm H₂O)'
const placementComp = 'Complications with placement'
const placementCompDetail = 'Complication description'
const notes = 'Other placement notes'
const timeRemoved = 'Time removed'
const removalNotes = 'Removal Notes'
const size = 'Size'
const stylet = 'Stylet utilized'
const preOx = 'Preoxygenation'
const cuffed = 'Cuffed tube'
const inflated = 'Cuff inflated'
const scopeType = 'Laryngoscopy type'
const scope = 'Laryngoscope blade'
const bladeSize = 'Laryngoscope blade size'
const viewGrade = 'Laryngoscopy view grade'
const securedWith = 'Secured with'
const depth = 'Tube depth (in cm)'
const magill = 'Forceps'
const eyeProtection = 'Eye protection'
const confirmation = 'Confirmation of Placement'
const cavityClear = 'Oropharyngeal cavity cleared of debris/excess fluids'
const preRemovalSpo2 = 'SpO₂ immediately prior to removal'
const deflated = 'Deflated prior to extubation'
const postRemovalSpo2 = 'SpO₂ after removal'
const removalComp = 'Complications with removal'
const removalCompDetail = 'Complication description'
const sealQuality = 'Mask seal quality'
const maskHolder = 'Mask holder'
const connectedTo = 'Connected to'
const lube = 'Tube lubricated'
const vaso = 'Vasoconstrictor'
const preWarmed = 'Tube Prewarmed'
const side = 'Side'
const name = 'Airway Type'

/**
 * Note there is a lot of repeated labels, but this is because the fieldMap is
 * not just for the form, but also for the order of items in the PDF in the
 * report selector.
 *
 * @type {Record<
 *   AirwayType,
 *   {
 *     name: string
 *     hideIfNoSedativesUsed?: true
 *     basic?: true
 *     fieldMap: Record<string, string>
 *   }
 * >}
 */
export const airwayTypeLookup = {
  cpap: {
    name: 'CPAP',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      maskType,
      peep,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      removalNotes,
    },
  },
  et: {
    name: 'Endotracheal Tube',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      size: 'Tube internal diameter (in mm)',
      stylet,
      preOx,
      cuffed,
      inflated,
      scopeType,
      scope,
      bladeSize,
      viewGrade,
      securedWith,
      depth,
      magill,
      eyeProtection,
      confirmation,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      cavityClear,
      preRemovalSpo2,
      deflated,
      postRemovalSpo2,
      removalComp,
      removalCompDetail,
      removalNotes,
    },
  },
  fm: {
    name: 'Face Mask',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      sealQuality,
      maskHolder,
      size,
      connectedTo,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      preRemovalSpo2,
      deflated,
      postRemovalSpo2,
      removalNotes,
    },
  },
  lma: {
    name: 'Laryngeal Mask Airway',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      preOx,
      size: 'Size of LMA',
      lube,
      securedWith,
      confirmation,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      cavityClear,
      preRemovalSpo2,
      deflated,
      postRemovalSpo2,
      removalComp,
      removalCompDetail,
      removalNotes,
    },
  },
  mad: {
    name: 'Mandibular Advancement Device',
    basic: true,
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      timeRemoved,
    },
  },
  nc: {
    name: 'Nasal Cannula',
    basic: true,
    fieldMap: {
      timePlaced,
      timeRemoved,
    },
  },
  nh: {
    name: 'Nasal Hood',
    basic: true,
    fieldMap: {
      timePlaced,
      timeRemoved,
    },
  },
  nt: {
    name: 'Nasopharyngeal Airway (NPA)',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      size,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      removalNotes,
    },
  },
  ntt: {
    name: 'Nasotracheal Tube',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      size: 'Tube internal diameter (in mm)',
      vaso,
      lube,
      preWarmed,
      side,
      preOx,
      cuffed,
      inflated,
      scopeType,
      scope,
      bladeSize,
      viewGrade,
      securedWith,
      depth,
      magill,
      eyeProtection,
      confirmation,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      cavityClear,
      preRemovalSpo2,
      deflated,
      postRemovalSpo2,
      removalComp,
      removalCompDetail,
      removalNotes,
    },
  },
  opa: {
    name: 'Oropharyngeal Airway (OPA)',
    hideIfNoSedativesUsed: true,
    fieldMap: {
      timePlaced,
      size,
      placementComp,
      placementCompDetail,
      notes,
      timeRemoved,
      removalNotes,
    },
  },
  other: {
    name: 'Other',
    fieldMap: {
      timePlaced,
      name,
      notes,
      timeRemoved,
      removalNotes,
    },
  },
}

/**
 * @param {AirwayType} type
 * @returns {Record<string, string>}
 */
export const getFieldLabelMapForAirwayType = type =>
  airwayTypeLookup[type] && airwayTypeLookup[type].fieldMap
